const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();

export const buildPacingData = (shows, venue, month, year, isCurrentMonth) => {
  const totalDays = daysInMonth(month, year);
  const now = new Date();
  const todayDay = isCurrentMonth ? now.getDate() : null;
  const isPastMonth =
    year < now.getFullYear() ||
    (year === now.getFullYear() && month < now.getMonth());
  const isFutureMonth =
    year > now.getFullYear() ||
    (year === now.getFullYear() && month > now.getMonth());

  const dailyRevMap = {};
  const settlementDays = new Set();

  shows.forEach((show) => {
    const d = new Date(show.date + "T12:00:00");
    const day = d.getDate();

    const profit = show.blended_profit || (show.forecast && show.forecast.profit) || 0;

    if (!dailyRevMap[day]) dailyRevMap[day] = 0;
    dailyRevMap[day] += profit || 0;

    if (show.status === "settled") settlementDays.add(day);
  });

  const monthlyTarget = venue.monthlyOverhead;
  const dailyTargetPace = monthlyTarget / totalDays;

  let cumulativeActual = 0;
  let cumulativeForecast = 0;
  let targetCrossDay = null;

  const lastActualDay = isPastMonth
    ? totalDays
    : isCurrentMonth
    ? todayDay
    : 0;

  const data = [];

  for (let day = 1; day <= totalDays; day++) {
    const dayRevenue = dailyRevMap[day] || 0;
    const linearPace = dailyTargetPace * day;
    const isToday = isCurrentMonth && day === todayDay;
    const isSettlement = settlementDays.has(day);
    const label = `${month + 1}/${day}`;

    const isActualDay = day <= lastActualDay;

    if (isActualDay) {
      cumulativeActual += dayRevenue;
      cumulativeForecast = cumulativeActual;
    } else {
      cumulativeForecast += dayRevenue;
    }

    const point = {
      day,
      label,
      linearPace: Math.round(linearPace),
      dailyRevenue: Math.round(dayRevenue),
      isToday,
      isSettlement,
    };

    if (isPastMonth || isActualDay) {
      point.actual = Math.round(cumulativeActual);
    } else {
      point.actual = null;
    }

    if (!isPastMonth && day > lastActualDay) {
      point.forecast = Math.round(cumulativeForecast);
      const band = cumulativeForecast * 0.08;
      point.confidenceUpper = Math.round(cumulativeForecast + band);
      point.confidenceLower = Math.round(cumulativeForecast - band);
    } else {
      point.forecast = null;
      point.confidenceUpper = null;
      point.confidenceLower = null;
    }

    if (
      !targetCrossDay &&
      (point.actual !== null ? point.actual : point.forecast) >= monthlyTarget
    ) {
      targetCrossDay = day;
    }

    data.push(point);
  }

  const bridgeDay = lastActualDay;
  if (bridgeDay > 0 && bridgeDay < totalDays && !isPastMonth) {
    const bridgePoint = data[bridgeDay - 1];
    const nextPoint = data[bridgeDay];
    if (bridgePoint && nextPoint) {
      bridgePoint.forecast = bridgePoint.actual;
      bridgePoint.confidenceUpper = bridgePoint.actual;
      bridgePoint.confidenceLower = bridgePoint.actual;
    }
  }

  // Split actual into above/below pace segments with overlap at transitions
  for (let i = 0; i < data.length; i++) {
    const pt = data[i];
    if (pt.actual === null) {
      pt.actualAbove = null;
      pt.actualBelow = null;
      continue;
    }

    const abovePace = pt.actual >= pt.linearPace;
    const prev = i > 0 ? data[i - 1] : null;
    const next = i < data.length - 1 ? data[i + 1] : null;
    const prevHasActual = prev && prev.actual !== null;
    const nextHasActual = next && next.actual !== null;
    const prevAbove = prevHasActual && prev.actual >= prev.linearPace;
    const nextAbove = nextHasActual && next.actual >= next.linearPace;

    const isTransitionFromPrev = prevHasActual && prevAbove !== abovePace;
    const isTransitionToNext = nextHasActual && nextAbove !== abovePace;

    if (abovePace) {
      pt.actualAbove = pt.actual;
      // Bridge to the next segment only at the last point before crossing below
      pt.actualBelow = isTransitionToNext ? pt.actual : null;
    } else {
      pt.actualBelow = pt.actual;
      // Bridge to the next segment only at the last point before crossing above
      pt.actualAbove = isTransitionToNext ? pt.actual : null;
    }
  }

  const actualToDate = lastActualDay > 0 ? cumulativeActual : null;
  const forecastFinal = isPastMonth
    ? cumulativeActual
    : cumulativeForecast;

  return {
    data,
    monthlyTarget,
    actualToDate,
    forecastFinal: Math.round(forecastFinal),
    targetCrossDay,
    todayDay: isCurrentMonth ? todayDay : null,
    isPastMonth,
    isFutureMonth,
    dailyTargetPace: Math.round(dailyTargetPace),
  };
};
